import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import apiRequest from '../../api/request';

const initialState = {
  assignableUsers: {
    data: [],
    role: null, // 'finance' | 'manager' | 'none'
    isCurrentMonth: true,
    totalTarget: 0,
    totalSlot: 0,
    totalUsers: 0,
    truncated: false,
    departments: [],
    wings: [],
    loading: false,
  },
  saving: false,
};

// Company-wide "set target for a CRO" - who you can set a target for depends on the logged-in
// user: Finance sees everyone, anyone else sees only their own active reports (direct +
// indirect, via user.reporting_head). See target_slot_fill.controller.js#resolveAssignAuthorization.
export const getAssignableUsers = createAsyncThunk(
  'targetManagement/getAssignableUsers',
  async ({ search, month, departmentId, wingId } = {}) => {
    const params = {};
    if (search) params.search = search;
    if (month) params.month = month;
    if (departmentId) params.departmentId = departmentId;
    if (wingId) params.wingId = wingId;
    const urlParams = new URLSearchParams(params);
    const res = await apiRequest({ method: 'get', url: `/target-slot-fill/assignable-users?${urlParams}` });
    return res;
  },
);

export const saveTarget = createAsyncThunk('targetManagement/saveTarget', async ({ assigned_to, target, slot, month }) => {
  const res = await apiRequest({
    method: 'post',
    url: '/target-slot-fill',
    data: { data: [{ assigned_to, target, slot }], month },
  });
  return res;
});

export const bulkSaveTargets = createAsyncThunk('targetManagement/bulkSaveTargets', async ({ entries, month }) => {
  // Every row in a single bulk upload shares the page's one month picker - stamp it onto each
  // entry so the backend groups/validates them against that month, not "now".
  const data = entries.map((e) => ({ ...e, date: month }));
  const res = await apiRequest({
    method: 'post',
    url: '/target-slot-fill/bulk',
    data: { data },
  });
  return res;
});

const targetManagementSlice = createSlice({
  name: 'targetManagement',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getAssignableUsers.pending, (state) => {
      state.assignableUsers.loading = true;
    });
    builder.addCase(getAssignableUsers.fulfilled, (state, action) => {
      const meta = action.payload?.meta || {};
      state.assignableUsers.loading = false;
      state.assignableUsers.data = action.payload?.data || [];
      state.assignableUsers.role = meta.role || null;
      state.assignableUsers.isCurrentMonth = meta.isCurrentMonth ?? true;
      state.assignableUsers.totalTarget = meta.totalTarget || 0;
      state.assignableUsers.totalSlot = meta.totalSlot || 0;
      state.assignableUsers.totalUsers = meta.totalUsers || 0;
      state.assignableUsers.truncated = !!meta.truncated;
      state.assignableUsers.departments = meta.departments || [];
      state.assignableUsers.wings = meta.wings || [];
    });
    builder.addCase(getAssignableUsers.rejected, (state) => {
      state.assignableUsers.loading = false;
    });

    builder.addCase(saveTarget.pending, (state) => {
      state.saving = true;
    });
    builder.addCase(saveTarget.fulfilled, (state) => {
      state.saving = false;
    });
    builder.addCase(saveTarget.rejected, (state) => {
      state.saving = false;
    });

    builder.addCase(bulkSaveTargets.pending, (state) => {
      state.saving = true;
    });
    builder.addCase(bulkSaveTargets.fulfilled, (state) => {
      state.saving = false;
    });
    builder.addCase(bulkSaveTargets.rejected, (state) => {
      state.saving = false;
    });
  },
});

export default targetManagementSlice.reducer;
